import {
  DateRangeParams,
  RequestResponse,
} from '@/common/types_and_interfaces.ts'
import { z } from 'zod'
import axios from 'axios'
import { parseRequestError } from '@/common/utilities.ts'
import { Student } from '@/queries/family_applications/requests'
import { UpdateStudentPayload } from '@/app/pages/applicants/applicant_details/hooks/useUpdateStudentDetails'
import { PaginatedApplicantPaymentResponseSchema } from './schema'
import { toUtcEndOfDayISOString, toUtcStartOfDayISOString } from '@/lib/utils'

type EmailOrPhone =
  | {
      email: string
      phone?: string
    }
  | {
      email?: string
      phone: string
    }
export type InviteApplicantRequest = {
  first_name?: string
  last_name?: string
  invite_callback_url?: string
  program_id?: number
  email?: string
} & EmailOrPhone

const InviteApplicantRespSchema = z.object({
  id: z.number(),
  status: z.number(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
})

export type InviteApplicantResponse = z.infer<typeof InviteApplicantRespSchema>

export const inviteApplicant = async (
  payload: InviteApplicantRequest
): Promise<RequestResponse<InviteApplicantResponse>> => {
  try {
    const response = await axios.post('/applicants', payload)
    return { data: InviteApplicantRespSchema.parse(response.data), error: null }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

export interface ApplicantPayload {
  address?: string
  address2?: string
  county?: string
  email?: string
  is_military_exemption?: boolean
  program_id: number
}

export async function updateApplicantInformation(
  applicationId: number,
  payload: ApplicantPayload
) {
  return axios.put(`family_application/${applicationId}`, payload)
}

export async function updateStudentInformation(
  student: Student,
  program_id: number,
  payload: Partial<UpdateStudentPayload>
) {
  return axios.put(
    `family_application/${student.scholarship_applications_id}/student_details`,
    {
      student_id: student.id,
      program_id,
      ...payload,
    }
  )
}

export type PaymentSortBy =
  | 'payment_id'
  | 'applicant_name'
  | 'student_name'
  | 'transaction_type'
  | 'provider_name'
  | 'total_amount'
  | 'invoice_quote'
  | 'status'
  | 'created_datetime'

type ApplicantPaymentsParams = {
  page?: number
  perPage?: number
  globalOnId?: number
  search?: string
  programId?: number
  applicantId?: number
  sortBy?: PaymentSortBy
  sortOrder?: 'asc' | 'desc'
}

export type UseApplicantPaymentsParams = ApplicantPaymentsParams &
  DateRangeParams

export const fetchApplicantPayments = async ({
  page = 1,
  perPage = 10,
  globalOnId = 1,
  search,
  programId,
  applicantId,
  sortBy,
  sortOrder,
  startDate,
  endDate,
}: UseApplicantPaymentsParams) => {
  try {
    const response = await axios.get(`/applicant_payments`, {
      params: {
        paginate: 1,
        per_page: perPage,
        page,
        global_on_id: globalOnId,
        search,
        program_id: programId,
        applicant_id: applicantId,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(startDate && {
          start_datetime: toUtcStartOfDayISOString(startDate),
          end_datetime: toUtcEndOfDayISOString(endDate ?? startDate),
        }),
      },
    })

    return PaginatedApplicantPaymentResponseSchema.parse(response.data)
  } catch (e) {
    throw parseRequestError(e)
  }
}
